import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { useSupabase } from './SupabaseContext';

type StaffHour = {
  weekday: number;
  start_time: string;
  end_time: string;
};

type WorkingHoursContextType = {
  staffHours: StaffHour[];
  refreshStaffHours: () => Promise<void>;
  refreshStaffHoursForStaff: (salonUserId: string) => Promise<void>;
  currentSalonUserId: string | null;
};

const WorkingHoursContext = createContext<WorkingHoursContextType>({
  staffHours: [],
  refreshStaffHours: async () => {},
  refreshStaffHoursForStaff: async () => {},
  currentSalonUserId: null,
});

export function WorkingHoursProvider({ children }: { children: React.ReactNode }) {
  const { supabase, salonId } = useSupabase();
  const [staffHours, setStaffHours] = useState<StaffHour[]>([]);
  const [currentSalonUserId, setCurrentSalonUserId] = useState<string | null>(null);

  const refreshStaffHoursForStaff = useCallback(async (salonUserId: string) => {
    if (!salonId || !salonUserId) return;
    const { data, error } = await supabase
      .from('staff_working_hours')
      .select('weekday, start_time, end_time')
      .eq('salon_user_id', salonUserId);
    if (!error && data) setStaffHours(data);
  }, [salonId, supabase]);

  const refreshStaffHours = useCallback(async () => {
    if (!salonId) return;
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    // Get salon_user_id for current user
    const { data: salonUser } = await supabase
      .from('salon_users')
      .select('id')
      .eq('user_id', user.id)
      .eq('salon_id', salonId)
      .maybeSingle();
    if (!salonUser?.id) return;

    setCurrentSalonUserId(salonUser.id);
    await refreshStaffHoursForStaff(salonUser.id);
  }, [salonId, supabase, refreshStaffHoursForStaff]);

  useEffect(() => {
    refreshStaffHours();
  }, [refreshStaffHours]);

  return (
    <WorkingHoursContext.Provider value={{ staffHours, refreshStaffHours, refreshStaffHoursForStaff, currentSalonUserId }}>
      {children}
    </WorkingHoursContext.Provider>
  );
}

export function useWorkingHours() {
  return useContext(WorkingHoursContext);
}